import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  TypographyProps,
  TooltipProps,
  tooltipClasses,
  Tooltip,
} from '@mui/material';
import {useState, useMemo, useEffect} from 'react';
import {Image, ImageProps} from '@jobseeker/lotus/components/Image';
import type {JobListProps} from '../type';
import {shallowEqual, useSelector} from 'react-redux';
import {
  DefaultJobLogo,
  Scope,
  HiristMan,
  HiristWoman,
  GreyDivider,
  StarIconHirist,
  RupeeGrey,
  YellowCrown,
} from '@jobseeker/lotus/icons';
import {getRandomColor} from '@jobseeker/lotus/theme';
import {dateToDuration} from '@jobseeker/lotus/utils/date';
import {FaStar, FaRegStar} from 'react-icons/fa';
import {GoDotFill} from 'react-icons/go';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {openUrl, deferTask} from '@jobseeker/common-core/utils/common';
import {AppState} from '@jobseeker/common-core/types';
import {customKebab} from '@jobseeker/lotus/utils';
import {formatSalary} from '@jobseeker/lotus/utils';
import JobTag from '../V2/JobTags';

const NUMBER_OF_TAGS_TO_SHOW_ON_LARGE_DEVICES = 5;
const NUMBER_OF_TAGS_TO_SHOW_ON_SMALL_DEVICES = 2;

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));
interface ImageWithFallbackProps extends ImageProps {
  fallback: any;
  itemId: any;
}
export const ImageWithFallback = ({
  fallback,
  alt,
  src,
  ...props
}: ImageWithFallbackProps) => {
  const [error, setError] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    setError(false);
  }, [src]);

  const randomColor = useMemo(() => getRandomColor(props.itemId), [src]);

  return (
    <>
      {!error ? (
        <Image
          key={alt}
          src={src}
          alt={alt}
          loading="lazy"
          onErrorCapture={() => setError(true)}
          style={{
            backgroundColor: '#fff',
            objectFit: 'contain',
          }}
          {...props}
        />
      ) : (
        <Box
          key={`${alt}_${randomColor}`}
          className="joblist__logo"
          sx={{
            background: randomColor,
            p: theme.spacing(1.5),
            height: 'min-content',
            lineHeight: 0,
          }}
        >
          <Image
            src={DefaultJobLogo}
            alt={'Logo'}
            width="25"
            height={'25'}
            objectFit="cover"
            quality={50}
          />
        </Box>
      )}
    </>
  );
};

export const Typography = styled(Typo)<any>((props: TypographyProps) => ({
  display: 'flex',
  alignItems: 'center',
}));
const TooltipHirist = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  ul: {
    margin: 0,
    padding: '14px 15px',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#1c2d41',
    maxWidth: '500px',
    color: '#fff',
    fontSize: 16,

    // border: '1px solid #dadde9',
  },
  '.MuiTooltip-arrow:before': {
    backgroundColor: '#1c2d41',
  },
}));

export const JoblistPaper = styled(Paper)<any>((props: any) => ({
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  gap: props.theme.spacing(1.5),
  // cursor: 'pointer',
  '.joblist__logo': {
    borderRadius: props.theme.spacing(1.5),
    border: '0.6px solid #7E7E7E33',
    '&:hover': {
      boxShadow: '0 5px 12px 0 rgba(0,0,0,.19)', // Shadow effect on hover
    },
  },
  '.joblist__right_element': {
    position: 'absolute',
    width: 'fit-content',
    right: props.theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    // padding: props.theme.spacing(2),
    top: 0,
    bottom: 0,
  },
  '.joblist__subtitle': {
    p: {
      wordBreak: 'break-all',
      color: props.theme.palette.text.secondary,
    },
  },
  '.save-icon': {
    display: 'none',
  },
  ':hover': {
    '.save-icon': {
      display: 'unset',
    },
  },
}));

const TagChip = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0,
  flexShrink: 1,
  overflow: 'hidden',
  padding: '3px 10px',
  borderRadius: '40px',
  border: '1px solid #E7EDF6',
  backgroundColor: '#F3F5FC',
  color: '#313132',
  fontSize: 12,
  fontWeight: 400,
  whiteSpace: 'nowrap',
  transition:
    'border 0.25s ease, background-color 0.25s ease, color 0.25s ease, order 0.25s ease',
});

const JobListComponent = (props: JobListProps) => {
  const ambitionBoxInfo = props?.jobData?.companyData?.ambitionBoxInfo;
  const showcasePage = props.jobData?.showcase;
  const createdTimeMs = props.jobData?.createdTimeMs;

  const rating = ambitionBoxInfo?.aggregateRatingOneDecimal;
  const reviewsCount = ambitionBoxInfo?.reviewsCount;
  const isRatingValid =
    typeof rating === 'number' && rating >= (props.jobData?.minRatingAb || 0);
  const hasReviews = typeof reviewsCount === 'number' && reviewsCount > 0;
  const isNotShowcase = !showcasePage;
  const isActiveJob =
    typeof createdTimeMs === 'number' &&
    Date.now() - createdTimeMs <= 180 * 24 * 60 * 60 * 1000;

  const showRating =
    ambitionBoxInfo &&
    isRatingValid &&
    hasReviews &&
    isNotShowcase &&
    isActiveJob;

  const theme = useTheme();
  const {
    date,
    jobData,
    // isDetailCard,
    showSaveIcon = true,
    showThreeDotMenu = false,
    showApplied = false,
    redirectionLink = '',
    jobPos,
    page = '',
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const IsLarge = useIsLargeDevice();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  useEffect(() => {
    setMounted(true);
    const handler = () => {
      setOpen(false);
    };
    window?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const dateString = useMemo(() => {
    return dateToDuration(date, true);
  }, [date]);

  const savedDateString = useMemo(() => {
    return dateToDuration(props.jobData?.savedDate as string, true);
  }, [props.jobData?.savedDate]);

  const salaryString = formatSalary(
    props.jobData?.minSal,
    props.jobData?.maxSal,
    props.jobData?.hideSal
  );

  const randomColor = useMemo(
    () => getRandomColor(props.jobData?.id),
    [props.companyImgUrl, props.jobData?.companyData]
  );
  const MainComponent = Card;

  const onJobClick = (event: any) => {
    // alert('test');
    event?.stopPropagation();
    if (props.onJobClick) {
      props.onJobClick(props.jobData);
    }
  };
  const categoryname = useSelector(
    (state: AppState) => state.app.context.navigation.slug,
    shallowEqual
  );

  const userSkills = useSelector(
    (state: AppState) =>
      (state.user.profile as any)?.skills as
        | string
        | Record<string, {tags: string}>
        | undefined,
    shallowEqual
  );

  const userTagNameSet = useMemo(() => {
    if (!userSkills) return new Set<string>();
    try {
      const parsed =
        typeof userSkills === 'string' ? JSON.parse(userSkills) : userSkills;
      return new Set(
        Object.values(parsed as Record<string, {tags: string}>)
          .map(s => s.tags?.toLowerCase() ?? '')
          .filter(Boolean)
      );
    } catch {
      return new Set<string>();
    }
  }, [userSkills]);

  const mandatoryTags = useMemo(() => {
    const tags = props.jobData?.tags || [];
    if (!tags.length) return [];

    const categoryMatch = tags.find((tag: any) => tag.name === categoryname);
    const otherTags = tags.filter(
      (tag: any) =>
        (!!tag?.isMandatory || tag.name === categoryname) &&
        tag.name !== categoryname
    );

    const result = categoryMatch ? [categoryMatch, ...otherTags] : otherTags;
    return result.length > 0 ? result : tags;
  }, [props.jobData?.tags]);

  const onSaveClick = (event: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    deferTask(() => {
      // setSaved(!saved);
      if (props.onSaveClick) {
        props.onSaveClick(props.jobData, !!props.jobData?.saved);
      }
    });
  };

  const isLarge = useIsLargeDevice();
  const imageLoader = ({src}: {src: string}) => {
    return props.companyImgUrl ?? '';
  };

  const remainingCountOnSmall = Math.max(
    0,
    mandatoryTags.length - NUMBER_OF_TAGS_TO_SHOW_ON_SMALL_DEVICES
  );
  const remainingCountOnLarge = Math.max(
    0,
    mandatoryTags.length - NUMBER_OF_TAGS_TO_SHOW_ON_LARGE_DEVICES
  );

  const title = customKebab(props?.jobData?.title);
  const newtitle = title.replace(/-\d+(?:-\d+)?-yrs$/, '');
  let jobLink = `/j/${newtitle}-${props?.jobData?.id}?ref=${props?.refCode}`;

  if (redirectionLink.length > 0) {
    jobLink = redirectionLink;
  }

  return (
    <Box
      display="flex"
      sx={{
        '&:hover .hover-box': {
          display: 'flex', // Show second box when hovering over the first
          boxShadow: 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
        },
        mb: -0.5,
      }}
    >
      <MainComponent
        className="joblist-card-v2"
        color="gray.dark"
        onClick={onJobClick}
        sx={{
          width:
            page === 'cv-analyzer'
              ? '100%'
              : {xxs: 'calc(100% - 16px)', md: 'calc(100% - 8px)'},
          position: 'relative',
          overflow: 'unset',
          transition: 'all ease 0.2s',
          cursor: 'pointer',
          ml: page === 'cv-analyzer' ? 0 : {xxs: 0, md: 1},

          '&:hover + .hover-box': {
            display: 'flex', // Show second box when hovering over the first
            boxShadow: 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
          },
          boxShadow: 'none',
        }}
      >
        <a
          href={`/j/${newtitle}-${props?.jobData?.id}?ref=${props?.refCode}${
            props?.prefCode ? `&pref=${props?.prefCode}` : ''
          }&jobPos=${jobPos}${props?.utmSource ? `&utm_source=${props.utmSource}` : ''}${props?.utmMedium ? `&utm_medium=${props.utmMedium}` : ''}${props?.utmCampaign ? `&utm_campaign=${props.utmCampaign}` : ''}`}
          target="_blank"
          style={{
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <JoblistPaper
            elevation={0}
            sx={{
              ...props.sx,
              pt: {xxs: 1.25},
              pb: {xxs: 2},
              pl: 1.25,

              borderRadius: 2,
              '&:hover': {
                boxShadow: 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
              },
              border:
                jobData?.applied && props.showApplied
                  ? '1px solid #49c5b6'
                  : 'none',

              pr: 0,
            }}
          >
            {jobData?.applied && props.showApplied ? (
              <Box
                sx={{
                  // display: 'flex',
                  position: 'absolute',
                  top: '-8px',
                  left: '45px',
                  color: '#fff',
                  bgcolor: '#49c5b6',
                  // border: '1px solid purple',
                  lineHeight: '14px',
                  borderRadius: '3px',
                  zIndex: 1,
                  display: 'inline-block',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '10px',
                    py: '2px',
                    px: '4px',
                    color: '#fff',
                    fontWeight: 500,
                    bgcolor: '#49c5b6',
                  }}
                >
                  APPLIED
                </Typography>
              </Box>
            ) : (
              <></>
            )}
            <Box
              sx={{
                display: 'flex',
                // position: 'absolute',
                // top: theme.spacing(1.25),
              }}
            >
              {!props.jobData?.confidential &&
              props.jobData?.companyData?.companyLogoFilePath ? (
                <ImageWithFallback
                  className="joblist__logo"
                  src={props.jobData?.companyData?.companyLogoFilePath ?? ''}
                  fallback={props.fallbackCompanyUrl}
                  alt={`logo_${jobData?.id}`}
                  width="50"
                  height={'50'}
                  itemId={jobData?.id}
                  quality={50}
                  placeholder="blur"
                  blurDataURL={
                    props.jobData?.companyData?.companyLogoFilePath ||
                    props.companyImgUrl ||
                    ''
                  }
                  // loader={imageLoader}
                />
              ) : (
                <Box
                  className="joblist__logo"
                  sx={{
                    background: randomColor,
                    p: theme.spacing(1.5),
                    height: 'min-content',
                    lineHeight: 0,
                  }}
                >
                  <Image
                    src={DefaultJobLogo}
                    alt={'Logo'}
                    width="25"
                    height={'25'}
                    objectFit="cover"
                    quality={50}
                  />
                </Box>
              )}
            </Box>
            <Box
              sx={{
                // display: 'flex',
                // flexDirection: 'column',
                // justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                pr: {md: '120px'},
              }}
              data-testid="job-list-1"
            >
              <Box
                // sx={{pr: {xxs: 3, sm: 12.2}}}
                display={'flex'}
                justifyContent={'space-between'}
                gap={2}
                alignItems={'flex-start'}
              >
                <Typography
                  component={'p'}
                  data-testid="job_title"
                  variant="subtitle2"
                  fontSize={{xxs: 14, md: 16}}
                  lineHeight="21px"
                  fontWeight={500}
                  width={'90%'}
                  sx={{
                    lineClamp: '3',
                    fontSize: 16,
                    fontWeight: 600,
                    '-webkit-line-clamp': '2',
                    '-webkit-box-orient': 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    display: 'block',
                    alignItems: 'center',
                  }}
                >
                  {props.title}
                  {isLarge && (
                    <TooltipHirist
                      title={props.isSaved ? 'Saved' : 'Save Job'}
                      arrow
                    >
                      <Box
                        component="span"
                        sx={{
                          color: 'primary.main',
                          position: 'relative',
                          top: '1px',
                          ml: 0.5,
                          display: 'inline-block',
                          fontSize: '14px',
                        }}
                        className="save-icon"
                        onClick={onSaveClick}
                      >
                        {props.isSaved ? <FaStar /> : <FaRegStar />}
                      </Box>
                    </TooltipHirist>
                  )}
                </Typography>
                {props.isPremiumJob && (
                  <Box
                    display={{md: 'none'}}
                    sx={{
                      position: 'absolute',
                      right: {xxs: 12, sm: 16},
                      top: {xxs: 11, sm: 16},
                      width: 16,
                      height: 16,
                    }}
                  >
                    <Image
                      className="joblist__premium_icon"
                      src={YellowCrown}
                      alt={'premium_icon'}
                      width={16}
                      height={16}
                      objectFit="cover"
                      quality={50}
                    />
                  </Box>
                )}
              </Box>
              <Box
                className="joblist__subtitle"
                sx={{
                  display: 'flex',
                  columnGap: 1,
                  rowGap: 0.5,
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  color: theme.palette.text.secondary,
                  flexWrap: 'nowrap',
                  mb: '-2px',
                  mt: '8px',
                }}
              >
                <Box
                  display={'flex'}
                  gap={1}
                  flex={1}
                  minWidth={0}
                  flexDirection={{xxs: 'column'}}
                >
                  <Box
                    sx={{display: 'flex', alignItems: 'center'}}
                    flexWrap={'wrap'}
                    rowGap={{sm: '2px', md: '6px'}}
                  >
                    <Typography
                      component={'span'}
                      data-testid="job_experience"
                      sx={{lineHeight: '16px'}}
                      variant="body2"
                      fontWeight="300"
                    >
                      {props.experiance}
                    </Typography>
                    <Typography component={'span'} color="#cecece" mx={1}>
                      <GoDotFill size={9} />
                    </Typography>
                    {salaryString && (
                      <>
                        <Typography
                          component={'span'}
                          data-testid="job_salary"
                          sx={{lineHeight: '16px'}}
                          variant="body2"
                          fontWeight="300"
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'baseline',
                            }}
                          >
                            <Image
                              src={RupeeGrey}
                              alt="rupee"
                              width={14}
                              height={14}
                              style={{
                                marginLeft: -1,
                                marginRight: 1,
                                position: 'relative',
                                top: 2,
                              }}
                            />
                            {salaryString}
                          </Box>
                        </Typography>
                        <Typography
                          component={'span'}
                          color="#cecece"
                          mx={1}
                          display={{xxs: 'none', md: 'flex'}}
                          alignItems="center"
                        >
                          <GoDotFill size={9} />
                        </Typography>
                        <Box
                          sx={{
                            flexBasis: '100%',
                            height: 0,
                            display: {md: 'none'},
                            mt: '6px',
                          }}
                        />
                      </>
                    )}

                    <Typography
                      sx={{
                        // maxWidth: '100%',
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'inline-block',
                        textOverflow: 'ellipsis',
                        lineHeight: '16px',
                        // whiteSpace: 'nowrap',
                      }}
                      component={'p'}
                      data-testid="job_location"
                      variant="body2"
                      fontWeight="300"
                    >
                      {props.location}
                    </Typography>
                    {showRating && !props.jobData?.confidential && (
                      <>
                        <Typography
                          component={'span'}
                          color="#cecece"
                          mx={1}
                          display={{xxs: 'none', sm: 'flex'}}
                        >
                          <GoDotFill size={9} />
                        </Typography>
                        <Box
                          display={{xxs: 'none', sm: 'flex'}}
                          alignItems={'center'}
                        >
                          <TooltipHirist
                            arrow
                            title={
                              <Typography
                                variant="subtitle3"
                                lineHeight={2.25}
                                className="set-tooltip"
                              >
                                Powered By AmbitionBox
                              </Typography>
                            }
                            placement="bottom-start"
                          >
                            <Box display="flex" alignItems="center">
                              <Image
                                src={StarIconHirist}
                                height={12}
                                width={12}
                                alt="star-icon"
                              />
                              <Typography
                                sx={{
                                  overflow: 'hidden',
                                  position: 'relative',
                                  display: 'inline-block',
                                  textOverflow: 'ellipsis',
                                  lineHeight: '16px',
                                  ml: '4px',
                                }}
                                component={'p'}
                                data-testid="company_rating"
                                variant="body2"
                                fontWeight="300"
                              >
                                {rating}
                              </Typography>

                              <Box
                                component={Image}
                                src={GreyDivider}
                                alt="grey-divider"
                                sx={{
                                  width: 10,
                                  height: 12,
                                  objectFit: 'contain',
                                  mb: 0.125,
                                }}
                              />
                              <Typography
                                sx={{
                                  overflow: 'hidden',
                                  position: 'relative',
                                  display: 'inline-block',
                                  textOverflow: 'ellipsis',
                                  lineHeight: '16px',
                                }}
                                component={'p'}
                                variant="body2"
                                fontWeight="300"
                                data-testid="company_reviews"
                              >
                                {`${reviewsCount?.toLocaleString()}+ Reviews`}
                              </Typography>
                            </Box>
                          </TooltipHirist>
                        </Box>
                      </>
                    )}
                  </Box>
                  <JobTag tags={props.additionalInfo} />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '10px',
                      overflow: 'hidden',
                      color: '#858585',
                    }}
                  >
                    {mandatoryTags.map((value: any, ti: number) => {
                      const isMatch = userTagNameSet.has(
                        value.name?.toLowerCase()
                      );
                      const visibilitySx =
                        ti < NUMBER_OF_TAGS_TO_SHOW_ON_SMALL_DEVICES
                          ? undefined
                          : ti < NUMBER_OF_TAGS_TO_SHOW_ON_LARGE_DEVICES
                            ? {display: {xxs: 'none', sm: 'inline-flex'}}
                            : {display: 'none'};
                      return (
                        <TagChip
                          key={ti}
                          component="span"
                          data-testid={`job_tag_${ti}`}
                          sx={{
                            ...(isMatch
                              ? {
                                  order: -1,
                                  border: '1px solid #97E6C9',
                                  backgroundColor: '#EEFFF9',
                                  color: '#00B171',
                                  fontSize: 12,
                                  fontWeight: 600,
                                }
                              : {}),
                            ...(visibilitySx || {}),
                          }}
                        >
                          <Box component="span">{value.name}</Box>
                        </TagChip>
                      );
                    })}
                    {!mounted ? (
                      <>
                        {remainingCountOnSmall > 0 && (
                          <TagChip
                            component="span"
                            sx={{
                              flexShrink: 0,
                              display: {xxs: 'inline-flex', sm: 'none'},
                            }}
                          >
                            +{remainingCountOnSmall}
                          </TagChip>
                        )}
                        {remainingCountOnLarge > 0 && (
                          <TagChip
                            component="span"
                            sx={{
                              flexShrink: 0,
                              display: {xxs: 'none', sm: 'inline-flex'},
                            }}
                          >
                            +{remainingCountOnLarge}
                          </TagChip>
                        )}
                      </>
                    ) : (
                      (isLarge
                        ? remainingCountOnLarge
                        : remainingCountOnSmall) > 0 && (
                        <TagChip component="span" sx={{flexShrink: 0}}>
                          +
                          {isLarge
                            ? remainingCountOnLarge
                            : remainingCountOnSmall}
                        </TagChip>
                      )
                    )}
                  </Box>
                  <Typography
                    component={'span'}
                    variant="body2"
                    fontWeight="300"
                    data-testid="date_posted_mobile"
                    display={{xxs: 'flex', md: 'none'}}
                    fontSize="11px"
                    color="text.secondary"
                    mt={0.5}
                  >
                    Posted {dateString}
                  </Typography>

                  {!isLarge && showRating && !props.jobData?.confidential && (
                    <Box
                      display="flex"
                      alignItems={'center'}
                      paddingRight={theme.spacing(1.25)}
                    >
                      <Box>
                        <Box display={{md: 'none'}} alignItems={'center'}>
                          <TooltipHirist
                            arrow
                            title={
                              <Typography
                                variant="subtitle3"
                                lineHeight={2.25}
                                className="set-tooltip"
                              >
                                Powered By AmbitionBox
                              </Typography>
                            }
                            placement="bottom-start"
                          >
                            <Box display="flex" alignItems="center">
                              <Box display={'flex'} marginBottom={'2px'}>
                                <Image
                                  src={StarIconHirist}
                                  height={12}
                                  width={12}
                                  alt="star-icon"
                                />
                              </Box>
                              <Typography
                                sx={{
                                  overflow: 'hidden',
                                  position: 'relative',
                                  display: 'inline-block',
                                  textOverflow: 'ellipsis',
                                  lineHeight: '16px',
                                  ml: '4px',
                                  color: '#6F6F6F',
                                }}
                                component={'span'}
                                data-testid="company_rating_mobile"
                                variant="body3"
                                fontWeight="300"
                              >
                                {rating}
                              </Typography>

                              <Box
                                component={Image}
                                src={GreyDivider}
                                alt="grey-divider"
                                sx={{
                                  width: 10,
                                  height: 12,
                                  objectFit: 'contain',
                                  mb: 0.2,
                                  display: 'inline-block',
                                  verticalAlign: 'middle',
                                }}
                              />

                              <Typography
                                sx={{
                                  overflow: 'hidden',
                                  position: 'relative',
                                  display: 'inline-block',
                                  textOverflow: 'ellipsis',
                                  lineHeight: '16px',
                                  color: '#6F6F6F',
                                }}
                                component={'span'}
                                variant="body3"
                                data-testid="company_reviews_mobile"
                                fontWeight="300"
                              >
                                {`${reviewsCount?.toLocaleString()}+ Reviews`}
                              </Typography>
                            </Box>
                          </TooltipHirist>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Box
              display={{xxs: 'none', md: 'flex'}}
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                flexDirection: 'column',
                alignItems: 'flex-end',
                justifyContent: 'center',
                gap: '12px',
                pr: 2,
              }}
            >
              {props.isPremiumJob && (
                <Image
                  className="joblist__premium_icon"
                  src={YellowCrown}
                  alt={'premium_icon'}
                  width={16}
                  height={16}
                  objectFit="cover"
                  quality={50}
                />
              )}
              <Typography
                component={'span'}
                variant="body2"
                fontWeight="300"
                data-testid="date_posted"
                fontSize="11px"
              >
                Posted {dateString}
              </Typography>
            </Box>
          </JoblistPaper>
        </a>
      </MainComponent>
      {isLarge && (jobData?.showcase || jobData?.diversity) ? (
        <Box
          className="hover-box"
          sx={{
            display: 'none', // Initially hidden
            padding: '8px',
            width: '32px',
            ml: -0.5,
            zIndex: 1,
            borderTopRightRadius: '8px',
            borderBottomRightRadius: '8px',
            cursor: 'pointer',
            backgroundColor: '#b4b9d8',
            transition: 'background-color 1s ease',
            '&:hover + .hover-box': {
              display: 'flex', // Show second box when hovering over the first
              boxShadow: 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
            },
          }}
        >
          <Box height={'100%'}>
            <Box
              height={
                jobData?.showcase && jobData?.diversity
                  ? '50%'
                  : jobData?.showcase
                    ? '100%'
                    : '0%'
              }
              display={jobData.showcase ? 'flex' : 'none'}
              justifyContent={'center'}
              alignItems={'center'}
              onClick={() => {
                openUrl(
                  `${jobData?.showcase?.showcaseUrl}?ref=?ref=jobfeedlist`
                );
              }}
            >
              <TooltipHirist
                arrow
                id="display_location"
                className="set-tooltip"
                title={
                  <Box fontSize={16} fontWeight="normal" padding="2px 1px">
                    Sneak peek inside office{' '}
                  </Box>
                }
              >
                <Image src={Scope} height={16} width={16} alt="alt" />
              </TooltipHirist>
            </Box>
            <Box
              height={
                jobData?.showcase && jobData?.diversity
                  ? '50%'
                  : jobData?.diversity
                    ? '100%'
                    : 0
              }
              display={jobData.diversity ? 'flex' : 'none'}
              justifyContent={'center'}
              alignItems={'center'}
              onClick={() => {
                openUrl(
                  `${jobData?.diversity?.diversityUrl}?ref=?ref=jobfeedlist`
                );
              }}
            >
              <TooltipHirist
                arrow
                id="display_location"
                className="set-tooltip"
                title={
                  <Box fontSize={16} fontWeight="normal" padding="2px 1px">
                    Explore diversity initiatives
                  </Box>
                }
              >
                <Box display={'flex'}>
                  <Image src={HiristMan} height={12} width={12} alt="alt" />
                  <Image
                    src={HiristWoman}
                    height={10}
                    width={10}
                    alt="alt"
                    style={{marginLeft: -4}}
                  />
                </Box>
              </TooltipHirist>
            </Box>
          </Box>
        </Box>
      ) : (
        <></>
      )}
    </Box>
  );
};

export default JobListComponent;
